import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash/fp";
import {
  Delete as DeleteIcon,
  ViewSplitHorizontal as ViewSplitHorizontalIcon,
  ViewSplitVertical as ViewSplitVerticalIcon,
} from "@actinc/dls/icons/";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  AppBar,
  Box,
  IconButton,
  List,
  ListItem,
  ListSubheader,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";

import {
  ControlledFormEditor,
  SRAccessibilityPanel,
  TTSAccessibilityPanel,
} from "../../../components/";
import {
  TTSStatusIcon,
  selectTTSIconType,
} from "../../../components/TTSStatusIcon";
import {
  useMemoCompare,
  useSessionStore,
  useSize,
  useStore,
} from "../../../services/hooks";
import * as itemService from "../../../services/item";
import {
  canPerformAction,
  getTaskPermissions,
  isAdmin,
} from "../../../services/utils";
import { removeFromContent } from "../../../services/contentUtils";
import { filterItemTemplatesByContentArea } from "../../../services/templates";
import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";

import { ChoiceInteractionForm } from "./ChoiceInteractionForm";
import { ExtendedTextInteractionForm } from "./ExtendedTextInteractionForm";
import { GapMatchInteractionForm } from "./GapMatchInteractionForm";
import { GraphicGapMatchInteractionForm } from "./GraphicGapMatchInteractionForm";
import { HotspotInteractionForm } from "./HotspotInteractionForm";
import { InlineChoiceInteractionForm } from "./InlineChoiceInteractionForm";
import { MatchInteractionForm } from "./MatchInteractionForm";
import { MatchTableInteractionForm } from "./MatchTableInteractionForm";
import { OrderInteractionForm } from "./OrderInteractionForm";
import { TextEntryInteractionForm } from "./TextEntryInteractionForm";
import { DemographicTextEntryInteractionForm } from "./DemographicTextEntryInteractionForm";

import { UserContext } from "../../../userProvider";
import {
  READY_COMPONENT_TYPE,
  useReadyContext,
} from "../../../providers/ReadyProvider";
import {
  calculateOverallStatus,
  hasProcessingDbTags,
  hasRetryableDbTags,
  TTS_DEBUG,
  TTSTagStatusDBEnum,
  useTTSFileStatusContext,
} from "../../../providers/TTSFileStatusProvider";

const ItemContentCard = ({ activeTasks, readonly }) => {
  const cardName = `ItemContentCard`;

  const [workspace, workspaceStore] = useSessionStore("workspace");
  const { orientation = "left-right", selectedTab = "Content" } =
    useMemoCompare(workspace?.cards.find((c) => c.i === cardName).state || {});

  const { control, getValues } = useFormContext();
  const {
    fields: interactions,
    insert,
    remove,
    update,
  } = useFieldArray({
    control,
    name: `item.interactions`,
  });

  const metadataConfiguration = useContext(MetadataConfigurationContext);
  const contentArea = useWatch({ control, name: "item.contentArea" });
  const itemTemplates = useMemo(
    () =>
      filterItemTemplatesByContentArea(metadataConfiguration?.templates)(
        contentArea,
      ),
    [metadataConfiguration, contentArea],
  );
  const { markReady } = useReadyContext();

  const tabHeaderRef = useRef();

  const [cardContentRef, setCardContentRef] = useState();
  const size = useSize(cardContentRef);

  const [editor, setEditor] = useState();

  const [item] = useStore("item");

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks?.item),
    [activeTasks?.item],
  );

  const user = useContext(UserContext);

  const canManageAccessibility = canPerformAction({
    inWorkflow: !!(activeTasks?.item || activeTasks?.passage),
    user,
  })(item)("manageAccessibility");

  const watchedItemAccessibilityElements = useWatch({
    control,
    name: "item.accessibilityElements",
  });
  const currentTTSInObjectDb = useMemo(
    () => watchedItemAccessibilityElements ?? [],
    [watchedItemAccessibilityElements],
  );
  // useWatch (reactive) for ttsTagStatuses so the icon re-renders when the
  // refetch effect lands fresh dbStatuses via setValue. getValues alone is
  // non-reactive and would leave retryableDb stale.
  const watchedItemTtsTagStatuses = useWatch({
    control,
    name: "item.ttsTagStatuses",
  });

  const ttsObjectStatus = useMemo(() => {
    const oldTTS = item?.accessibilityElements ?? [];
    const isUnchanged =
      currentTTSInObjectDb.length === oldTTS.length &&
      currentTTSInObjectDb.every(
        (tag, index) => tag.identifier === oldTTS[index]?.identifier,
      );

    return isUnchanged && oldTTS.length > 0
      ? TTSTagStatusDBEnum.SAVED
      : TTSTagStatusDBEnum.PENDING;
  }, [currentTTSInObjectDb, item?.accessibilityElements]);

  const { ttsTagStatusMap } = useTTSFileStatusContext();

  const ttsTagStatus = useMemo(() => {
    const ttsFilesMap = ttsTagStatusMap[getValues("item.id")];
    return calculateOverallStatus(ttsFilesMap, currentTTSInObjectDb.length);
  }, [ttsTagStatusMap, currentTTSInObjectDb.length, getValues]);

  useEffect(() => {
    if (!TTS_DEBUG) return;
    const liveMap = ttsTagStatusMap[getValues("item.id")] ?? {};
    const liveEntries = Object.entries(liveMap).filter(
      ([k]) => k !== "__requestStatus",
    );
    const inProgress = liveEntries.filter(([, v]) =>
      ["PROCESSING", "ENQUEUED"].includes(v.status),
    ).length;
    const failed = liveEntries.filter(([, v]) => v.status === "FAILED").length;
    const completed = liveEntries.filter(
      ([, v]) => v.status === "GENERATED",
    ).length;
    const total = (item?.accessibilityElements ?? []).length;
    const requestStatus = liveMap["__requestStatus"];
    // NOTE: inProgress is always 0 — individual tags only receive GENERATED via WebSocket,
    // never PROCESSING/ENQUEUED. PROCESSING state comes from __requestStatus, not tag messages.
    console.info(
      `[TTS Tab - item] live: ${completed} GENERATED / ${inProgress} PROCESSING / ${failed} FAILED` +
        ` | totalExpected: ${currentTTSInObjectDb.length} (savedTotal: ${total})` +
        ` | overallStatus: ${ttsTagStatus} | __requestStatus: ${requestStatus?.status ?? "none"}`,
    );
  }, [
    ttsTagStatusMap,
    ttsTagStatus,
    ttsObjectStatus,
    getValues,
    item?.accessibilityElements,
    currentTTSInObjectDb.length,
  ]);

  const ttsIconType = useMemo(() => {
    const dbTagStatuses = watchedItemTtsTagStatuses ?? [];
    const liveMap = ttsTagStatusMap[getValues("item.id")] ?? {};
    return selectTTSIconType({
      hasTTSTags:
        currentTTSInObjectDb.length > 0 ||
        (item?.accessibilityElements ?? []).length > 0,
      ttsTagStatus,
      ttsObjectStatus,
      retryable: hasRetryableDbTags(dbTagStatuses, liveMap),
      processingDb: hasProcessingDbTags(dbTagStatuses, liveMap),
    });
  }, [
    watchedItemTtsTagStatuses,
    ttsTagStatusMap,
    currentTTSInObjectDb.length,
    item?.accessibilityElements,
    ttsTagStatus,
    ttsObjectStatus,
    getValues,
  ]);

  const tabs = useMemo(
    () =>
      [
        {
          filter:
            !permissions.includes("accessibility") ||
            isAdmin(user)(item?.program),
          label: "Edit Content",
          value: "Content",
          disabled: false,
        },
        {
          filter:
            (!activeTasks.item &&
              item?._actions.includes("viewAccessibility")) ||
            permissions.includes("accessibility"),
          label: (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              TTS
              <TTSStatusIcon type={ttsIconType} debugContext="item" />
            </Box>
          ),
          value: "TTS",
          disabled: editor?.state !== "ready" && selectedTab === "Content",
        },
        {
          filter:
            (!activeTasks.item &&
              item?._actions.includes("viewAccessibility")) ||
            permissions.includes("accessibility"),
          label: "SR",
          value: "SR",
          disabled: editor?.state !== "ready" && selectedTab === "Content",
        },
      ].filter(({ filter }) => filter),
    [
      activeTasks.item,
      item?._actions,
      item?.ttsTagStatuses,
      permissions,
      editor?.state,
      selectedTab,
      ttsTagStatus,
      ttsTagStatusMap,
      ttsObjectStatus,
      ttsIconType,
      currentTTSInObjectDb.length,
    ],
  );

  useEffect(() => {
    if (!tabs.some((tab) => tab.value === selectedTab))
      workspaceStore.selectTab(cardName)(null, tabs.at(0).value);
  }, [tabs]);

  const imageAssets = useMemoCompare(item?.imageAssets);
  const mediaAssets = useMemoCompare(item?.mediaAssets);

  const debouncedSelectTab = _.debounce(
    300,
    workspaceStore.selectTab(cardName),
  );

  const createInteraction =
    (ed) =>
    (_eventInfo, { type }) => {
      const interaction = itemService.createInteraction(getValues("item"))(
        type,
      );
      const position = itemService.getInteractionPosition(
        getValues("item.interactions"),
      )(interaction);

      insert(position, interaction);

      // For Gap Match interactions, we need to insert Elements and the first target
      if (["Gap Match"].includes(type)) {
        insertInteractionElements(ed)({
          elements: ["Target1", "Elements"],
          interaction,
          type,
        });
      } else {
        insertInteractionElements(ed)({
          interaction,
          type,
        });
      }
    };

  const deleteInteraction = (interaction) => (index) => () => {
    remove(index);
    _.pipe([
      removeFromContent(
        `.interaction[data-interaction-name="${interaction.identifier}"]`,
      ),
      (updatedData) => editor.data.set(updatedData),
    ])(editor.data.get());
  };

  const getTabHeight = () => {
    const cardHeight = size?.height || 0;
    const tabHeaderHeight = tabHeaderRef.current
      ? tabHeaderRef.current.getBoundingClientRect().height
      : 0;
    return `${cardHeight - tabHeaderHeight}px`;
  };

  const insertInteractionElements =
    (ed) =>
    ({ elements, interaction, type }) => {
      ed.fire("cde:insertInteractionElements", {
        elements,
        interaction,
        type,
      });
    };

  const onReady = (ed) => {
    setEditor(ed);
    console.info("Item Content editor is ready");
    ed.on("cde:createInteraction", createInteraction(ed));
    markReady([item], READY_COMPONENT_TYPE.ITEM);
  };

  if (!item || !getValues("item.id") || permissions.includes("externalReview"))
    return null;

  return (
    <>
      {item.passthrough ? (
        <Typography>This information is unavailable</Typography>
      ) : (
        <Box
          ref={setCardContentRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            "& .qti-visually-hidden.srNotInsertedText": {
              display: "none",
            },
          }}
        >
          <TabContext value={selectedTab}>
            <AppBar
              color="transparent"
              elevation={1}
              position="static"
              ref={tabHeaderRef}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <TabList variant="scrollable" onChange={debouncedSelectTab}>
                {tabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                    disabled={tab.disabled}
                  />
                ))}
              </TabList>

              <ToggleButtonGroup
                exclusive
                onChange={workspaceStore.setOrientation(cardName)}
                size="small"
                value={orientation}
              >
                <ToggleButton value="left-right">
                  <ViewSplitVerticalIcon />
                </ToggleButton>
                <ToggleButton value="top-bottom">
                  <ViewSplitHorizontalIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </AppBar>
            <TabPanel
              sx={{ display: "flex", overflowY: "auto", padding: 0 }}
              value="Content"
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection:
                    orientation === "left-right" ? "row" : "column",
                  flexGrow: 1,
                  height: getTabHeight,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: "1 1 40%",
                    height: "100%",
                    minHeight: 300,
                    overflowX: "hidden",
                    overflowY: "auto",
                    width: "100%",
                    '& .ck.ck-editor[role="application"]': {
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                      '& .ck.ck-editor__main[role="presentation"]': {
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                        height: 100,
                        '& .ck.ck-content.ck-editor__editable[role="textbox"]':
                          {
                            flexGrow: 1,
                            "& .qti-visually-hidden": {
                              display: "none",
                            },
                            "& figure.table": {
                              marginLeft: "0px",
                            },
                            "& thead tr th": {
                              textAlign: "center",
                              verticalAlign: "bottom",
                            },
                            "& tr#perspectiveTableHeadRow th, tr#perspectiveTableHeadRow td":
                              {
                                textAlign: "left",
                                verticalAlign: "middle",
                              },
                            "& tr#perspectiveTableBodyRow th, tr#perspectiveTableBodyRow td":
                              {
                                textAlign: "left",
                                verticalAlign: "middle",
                              },
                          },
                      },
                    },
                  }}
                >
                  <ControlledFormEditor
                    _actions={item?._actions}
                    assets={{
                      images: imageAssets,
                      medias: mediaAssets,
                    }}
                    comments={"item.contentComments"}
                    contentArea={contentArea}
                    control={control}
                    deleteInteraction={deleteInteraction}
                    id="item.content"
                    interactions={interactions}
                    name="item.content"
                    onReady={onReady}
                    options={[
                      "applyTemplate",
                      "createInteraction",
                      "content",
                      "createLineRef",
                      "insertMedia",
                      permissions.includes("suggestChanges") &&
                        "suggestChanges",
                      permissions.includes("acceptChanges") && "acceptChanges",
                    ].filter(Boolean)}
                    readonly={readonly}
                    suggestions={"item.contentSuggestions"}
                    templates={itemTemplates}
                    type="item"
                  />
                </Box>
                <Box
                  sx={{
                    display: interactions.length ? "flex" : "none",
                    flex: "1 1 60%",
                    height: "100%",
                    overflowY: "auto",
                    // CKEditor 5's StickyPanelView pins the toolbar with
                    // `position: fixed; top: 0`. `react-grid-layout` positions
                    // each grid item with `transform: translate(...)`, which
                    // creates a containing block for fixed-positioned
                    // descendants — so the toolbar pins to the grid item's
                    // top-left, not the viewport, and lands on top of the
                    // foil's text content. Forcing `position: sticky` here
                    // uses the inner scroll container as the reference and
                    // pins correctly.
                    "& .ck.ck-sticky-panel .ck-sticky-panel__content_sticky": {
                      position: "sticky !important",
                      top: "0 !important",
                      left: "auto !important",
                      width: "auto !important",
                    },
                  }}
                >
                  <List
                    dense={true}
                    sx={{
                      backgroundColor: "background.paper",
                      flexDirection: "column",
                      flexGrow: 1,
                      overflowX: "auto",
                      overflowY: "auto",
                      paddingTop: 0,
                      position: "relative",
                    }}
                  >
                    {!!interactions.length &&
                      interactions.map((interaction, index) => {
                        const Component = interactionTypes?.find(
                          (o) => o.__typename === interaction.__typename,
                        ).component;
                        const componentProps = {
                          activeTasks,
                          imageAssets: imageAssets,
                          index,
                          insertInteractionElements:
                            insertInteractionElements(editor),
                          interaction,
                          readonly,
                          update,
                        };
                        return (
                          <React.Fragment key={interaction.id}>
                            <ListSubheader
                              sx={{
                                alignItems: "center",
                                borderTop: "1px solid lightgrey",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                zIndex: 10,
                              }}
                            >
                              <Typography variant="subtitle1">
                                Interaction {interaction.identifier}
                              </Typography>
                              {!readonly && (
                                <Tooltip title="Delete Interaction">
                                  <IconButton
                                    aria-label="delete"
                                    onClick={deleteInteraction(interaction)(
                                      index,
                                    )}
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </ListSubheader>
                            <ListItem>
                              <Component {...componentProps} />
                            </ListItem>
                          </React.Fragment>
                        );
                      })}
                  </List>
                </Box>
              </Box>
            </TabPanel>
            <TabPanel
              sx={{ display: "flex", overflowY: "auto", padding: 0 }}
              value="TTS"
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection:
                    orientation === "left-right" ? "row" : "column",
                  flexGrow: 1,
                  height: getTabHeight,
                }}
              >
                <TTSAccessibilityPanel
                  objectType="item"
                  orientation={orientation}
                  readonly={!canManageAccessibility}
                />
              </Box>
            </TabPanel>
            <TabPanel
              sx={{ display: "flex", overflowY: "auto", padding: 0 }}
              value="SR"
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection:
                    orientation === "left-right" ? "row" : "column",
                  flexGrow: 1,
                  height: getTabHeight,
                }}
              >
                <SRAccessibilityPanel
                  objectType="item"
                  orientation={orientation}
                  readonly={!canManageAccessibility}
                />
              </Box>
            </TabPanel>
          </TabContext>
        </Box>
      )}
    </>
  );
};

const interactionTypes = [
  {
    component: ChoiceInteractionForm,
    __typename: "ChoiceInteraction",
  },
  {
    component: DemographicTextEntryInteractionForm,
    __typename: "DemographicTextEntryInteraction",
  },
  {
    component: ExtendedTextInteractionForm,
    __typename: "ExtendedTextInteraction",
  },
  {
    component: GapMatchInteractionForm,
    __typename: "GapMatchInteraction",
  },
  {
    component: GraphicGapMatchInteractionForm,
    __typename: "GraphicGapMatchInteraction",
  },
  {
    component: HotspotInteractionForm,
    __typename: "HotspotInteraction",
  },
  {
    component: InlineChoiceInteractionForm,
    __typename: "InlineChoiceInteraction",
  },
  {
    component: MatchInteractionForm,
    __typename: "MatchInteraction",
  },
  {
    component: MatchTableInteractionForm,
    __typename: "MatchTableInteraction",
  },
  {
    component: OrderInteractionForm,
    __typename: "OrderInteraction",
  },
  {
    component: TextEntryInteractionForm,
    __typename: "TextEntryInteraction",
  },
];

export { ItemContentCard };
